import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject, isDevMode } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { from, Observable, switchMap, catchError, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { ErrorHandlerService } from '@core/services/error-handler.service';

/**
 * HTTP Interceptor to attach Bearer token to API requests
 * Automatically acquires token from MSAL and adds to Authorization header
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const msalService = inject(MsalService);

  if (isDevMode()) { console.log('🎫 Auth Interceptor invoked:', {
    url: req.url,
    apiUrl: environment.apiUrl,
    startsWithApiUrl: req.url.startsWith(environment.apiUrl)
  }); }

  // Check if request is to our API (don't add token for external URLs)
  if (!req.url.startsWith(environment.apiUrl)) {
    if (isDevMode()) { console.log('⏭️ Skipping auth - URL does not start with API base URL'); }
    return next(req);
  }

  // Get the active account
  let activeAccount = msalService.instance.getActiveAccount();
  if (isDevMode()) { console.log('🎫 Active account check:', { activeAccount: !!activeAccount }); }

  // If no active account, try to set the first available account as active
  if (!activeAccount) {
    const accounts = msalService.instance.getAllAccounts();
    if (isDevMode()) { console.log('⚠️ No active account, checking all accounts:', { accountCount: accounts.length }); }
    
    if (accounts.length > 0) {
      if (isDevMode()) { console.log('✅ Setting first account as active:', accounts[0].username); }
      msalService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    } else {
      if (isDevMode()) { console.log('❌ No accounts available, proceeding without token'); }
      return next(req);
    }
  }

  // Acquire token silently
  const tokenRequest = {
    scopes: environment.azureAd.scopes,
    account: activeAccount
  };

  if (isDevMode()) { console.log('🎫 Auth Interceptor: Acquiring token for API call', {
    url: req.url,
    scopes: tokenRequest.scopes
  }); }

  return from(msalService.instance.acquireTokenSilent(tokenRequest)).pipe(
    switchMap((response) => {
      if (isDevMode()) { console.log('✅ Token acquired successfully:', {
        scopes: response.scopes,
        expiresOn: response.expiresOn,
        tokenLength: response.accessToken.length
      }); }
      
      // Clone the request and add the authorization header
      const clonedRequest = req.clone({
        setHeaders: {
          Authorization: `Bearer ${response.accessToken}`
        }
      });
      if (isDevMode()) { console.log('✅ Authorization header added to request'); }
      return next(clonedRequest);
    }),
    catchError((error) => {
      console.error('❌ Token acquisition failed in interceptor:', error);
      console.error('Error details:', {
        errorCode: error.errorCode,
        errorMessage: error.errorMessage,
        subError: error.subError
      });
      // Let request proceed without token (will get 401 from backend)
      return next(req);
    })
  );
};

/**
 * Error interceptor to handle HTTP errors globally
 */
export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const errorHandler = inject(ErrorHandlerService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only show snackbar for errors that aren't handled by components
      // Components can skip this by adding 'X-Skip-Error-Interceptor' header
      if (!req.headers.has('X-Skip-Error-Interceptor')) {
        errorHandler.handleError(error);
      }
      return throwError(() => error);
    })
  );
};
